import type {FC} from "react";
import {useMemo} from "react";
import {Card} from "@src/components/card/card";
import {Container, ContentContainer, PageTitle} from "@src/core/styles";
import cardImage from "@src/core/images/media.png";
import {Link} from "react-router-dom";
import {
    CardGroup,
    CardItem,
    Group,
    StatisticsContainer,
    StatisticsTitle,
    StatisticsItem,
    StatisticsLabel,
    StatisticsValue,
    CardItemRow,
    WidgetsRow,
    WidgetCard,
    WidgetTitle,
    LimitItem,
    LimitHeader,
    LimitLabel,
    LimitValues,
    ProgressBarTrack,
    ProgressBarFill,
    QuickActionsGrid,
    QuickActionLink,
    StatusDot,
    RouteStatusItem,
    RouteStatusCount,
} from "./dashboard.styles";
import {APP_ROUTES} from "@src/core/constants";
import {Card as ProfileCard} from "@src/pages/price-page/components/card";
import {useQuery} from "@tanstack/react-query";
import {Authentication, Workspace} from "@src/core/services";
import {getActiveFinishDate} from "@src/core/utils";
import CardImg0 from "@src/core/images/tariff-starter.jpg";
import CardImg1 from "@src/core/images/tariff-small.jpg";
import CardImg2 from "@src/core/images/tariff-light.jpg";
import CardImg3 from "@src/core/images/tariff-medium.jpg";
import CardImg4 from "@src/core/images/tariff-large.jpg";
import {ReactComponent as AddIcon} from "@src/core/icons/add.svg";
import {ReactComponent as SettingsIcon} from "@src/core/icons/settings.svg";

export const Dashboard: FC = () => {
    const {data: info} = useQuery({
        queryKey: ["info"],
        queryFn: () => Authentication.info(),
    });

    const {data: statistics} = useQuery({
        queryKey: ["statistics"],
        queryFn: () => Authentication.statistics(),
    });

    const {data: workspacesData} = useQuery({
        queryKey: ["workspaces"],
        queryFn: () => Workspace.getAll(),
    });

    const tariff = info?.data.tariff;
    let activeFinishTariff =
        tariff?.active_finish && getActiveFinishDate(tariff?.active_finish);
    if (null === activeFinishTariff) {
        activeFinishTariff = 'бессрочно';
    } else {
        activeFinishTariff = 'до ' + activeFinishTariff;
    }

    const getImage = (tariffname: string) => {
        switch (tariffname) {
            case "Начальный":
                return CardImg0;
            case "Small":
                return CardImg1;
            case "Light":
                return CardImg2;
            case "Medium":
                return CardImg3;
            case "Large":
                return CardImg4;
        }
    };

    const getTariffImage = tariff?.name && getImage(tariff?.name);

    const routeStats = useMemo(() => {
        if (!workspacesData?.data.items) return {active: 0, inactive: 0, total: 0};
        let active = 0;
        let inactive = 0;
        workspacesData.data.items.forEach((ws) => {
            ws.routes.forEach((route) => {
                if (route.active) {
                    active++;
                } else {
                    inactive++;
                }
            });
        });
        return {active, inactive, total: active + inactive};
    }, [workspacesData]);

    const routesUsedPercent = tariff?.api_count
        ? ((statistics?.data.routes_count ?? 0) / tariff.api_count) * 100
        : 0;

    return (
        <Container>
            <ContentContainer>
                <CardGroup>
                    <CardItem>
                        <PageTitle>Панель управления</PageTitle>
                        <CardItemRow>
                            <Link to={`${APP_ROUTES.WORKSPACE}`}>
                                <Card
                                    title="Управление маршрутами"
                                    description="Добавление, редактирование, удаление маршрутов"
                                    image={cardImage}
                                />
                            </Link>
                            {statistics?.data && (
                                <StatisticsContainer>
                                    <StatisticsTitle>Статистика</StatisticsTitle>
                                    <StatisticsItem>
                                        <StatisticsLabel>Пространства</StatisticsLabel>
                                        <StatisticsValue>{statistics.data.workspaces_count}</StatisticsValue>
                                    </StatisticsItem>
                                    <StatisticsItem>
                                        <StatisticsLabel>Маршрутов</StatisticsLabel>
                                        <StatisticsValue>{statistics.data.routes_count}</StatisticsValue>
                                    </StatisticsItem>
                                    <StatisticsItem>
                                        <StatisticsLabel>Запросов</StatisticsLabel>
                                        <StatisticsValue>{statistics.data.requests_count}</StatisticsValue>
                                    </StatisticsItem>
                                </StatisticsContainer>
                            )}
                        </CardItemRow>
                    </CardItem>

                    <WidgetsRow>
                        {tariff && statistics?.data && (
                            <WidgetCard>
                                <WidgetTitle>Лимиты тарифа «{tariff.name}»</WidgetTitle>
                                <LimitItem>
                                    <LimitHeader>
                                        <LimitLabel>API маршрутов</LimitLabel>
                                        <LimitValues>
                                            {statistics.data.routes_count} / {tariff.api_count}
                                        </LimitValues>
                                    </LimitHeader>
                                    <ProgressBarTrack>
                                        <ProgressBarFill percent={routesUsedPercent}/>
                                    </ProgressBarTrack>
                                </LimitItem>
                                <LimitItem>
                                    <LimitHeader>
                                        <LimitLabel>Запросов в минуту</LimitLabel>
                                        <LimitValues>{tariff.request_count}</LimitValues>
                                    </LimitHeader>
                                    <ProgressBarTrack>
                                        <ProgressBarFill percent={0} color="#2e7d32"/>
                                    </ProgressBarTrack>
                                </LimitItem>
                            </WidgetCard>
                        )}

                        <WidgetCard>
                            <WidgetTitle>Статус маршрутов</WidgetTitle>
                            <RouteStatusItem>
                                <span><StatusDot active={true}/>Активных</span>
                                <RouteStatusCount>{routeStats.active}</RouteStatusCount>
                            </RouteStatusItem>
                            <RouteStatusItem>
                                <span><StatusDot active={false}/>Неактивных</span>
                                <RouteStatusCount>{routeStats.inactive}</RouteStatusCount>
                            </RouteStatusItem>
                            <RouteStatusItem>
                                <span>Всего</span>
                                <RouteStatusCount>{routeStats.total}</RouteStatusCount>
                            </RouteStatusItem>
                        </WidgetCard>

                        <WidgetCard>
                            <WidgetTitle>Быстрые действия</WidgetTitle>
                            <QuickActionsGrid>
                                <Link to={`${APP_ROUTES.WORKSPACE}/create`}>
                                    <QuickActionLink>
                                        <AddIcon/>
                                        <span>Создать пространство</span>
                                    </QuickActionLink>
                                </Link>
                                <Link to={`${APP_ROUTES.SETTINGS}`}>
                                    <QuickActionLink>
                                        <SettingsIcon/>
                                        <span>Настройки аккаунта</span>
                                    </QuickActionLink>
                                </Link>
                            </QuickActionsGrid>
                        </WidgetCard>
                    </WidgetsRow>

                    <CardItem>
                        <PageTitle>Ваш профиль</PageTitle>
                        <Group>
                            {tariff && (
                                <ProfileCard
                                    title={tariff.name}
                                    badgeText={`Действует ${activeFinishTariff}`}
                                    src={getTariffImage ?? ""}
                                    period={tariff.description}
                                    api={tariff.api_count}
                                    requests={tariff.request_count}
                                    buttonText="Улучшить профиль"
                                    handleButton={10}
                                />
                            )}
                            <Link to={`${APP_ROUTES.SETTINGS}`}>
                                <Card
                                    title="Настройки"
                                    description="Редактирование ФИО, смена пароля"
                                />
                            </Link>
                        </Group>
                    </CardItem>
                </CardGroup>
            </ContentContainer>
        </Container>
    );
};
